import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { UserCompaniesResponse } from '../../../../typings/api/skymap/rest/v0/company';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { useOptionalAuthState } from './auth-state';

export type UserCompanies = UserCompaniesResponse['data'];

export type UserCompaniesStateContextValue = {
  userCompanies: UserCompanies | undefined;
  fetchUserCompanies: (userId: string, organizationAdmin: boolean) => Promise<void>;
  isCompaniesLoaded: () => boolean;
};

export const UserCompaniesStateContext = React.createContext<
  UserCompaniesStateContextValue | undefined
>(undefined);

type Props = {
  children: React.ReactNode;
};

export const UserCompaniesStateProvider = ({ children }: Props) => {
  const authState = useOptionalAuthState();
  const [userCompanies, setUserCompanies] = useState<UserCompanies | undefined>();

  const fetchUserCompaniesInternal = useCallback(
    async (userId: string, organizationAdmin: boolean): Promise<void> => {
      if (!userId) {
        setUserCompanies(undefined);
        return;
      }

      if (organizationAdmin) {
        setUserCompanies([]);
      }

      const { data: companies } = await SkyMapAxiosServiceFactory.instance
        .createCompanyServiceV0()
        .fetchUserCompanies({ query: { id: userId } });

      setUserCompanies(companies);
    },
    [],
  );

  useEffect(() => {
    if (authState?.status === 'authenticated' && authState.user && userCompanies === undefined) {
      void fetchUserCompaniesInternal(
        authState.user.id,
        authState.user.isOrganizationAdmin ?? false,
      );
    }
  }, [
    authState?.status,
    authState?.user,
    authState?.user?.id,
    authState?.user?.isOrganizationAdmin,
    fetchUserCompaniesInternal,
    userCompanies,
  ]);

  const fetchUserCompanies = useCallback(
    async (userId: string, organizationAdmin: boolean): Promise<void> => {
      await fetchUserCompaniesInternal(userId, organizationAdmin);
    },
    [fetchUserCompaniesInternal],
  );

  const isCompaniesLoaded = useCallback(() => !!userCompanies, [userCompanies]);

  const value = useMemo<UserCompaniesStateContextValue>(
    () => ({
      userCompanies,
      fetchUserCompanies,
      isCompaniesLoaded,
    }),
    [userCompanies, fetchUserCompanies, isCompaniesLoaded],
  );

  return (
    <UserCompaniesStateContext.Provider value={value}>
      {children}
    </UserCompaniesStateContext.Provider>
  );
};

export const useUserCompaniesState = (): UserCompaniesStateContextValue => {
  const context = React.useContext(UserCompaniesStateContext);
  if (context === undefined) {
    throw new Error('useUserCompaniesState must be used within a UserCompaniesStateProvider');
  }
  return context;
};
